import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const formatNotificationTime = (value) => {
  if (!value) return "";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const resolveNotificationRoute = (notification) => {
  if (!notification) return "/dashboard";

  if (notification.type === "report-submitted" && notification.relatedId) {
    return `/reports/${notification.relatedId}`;
  }

  if (notification.type === "travel-created" || notification.type === "travel-status") {
    return "/admin/travel";
  }

  if (notification.relatedId) {
    return `/complaints?complaintId=${notification.relatedId}`;
  }

  return "/complaints";
};

const buildCards = (stats) => ([
  {
    title: "Total Groups",
    value: stats.totalGroups || 0,
    accent: "text-purple-700",
    bg: "from-purple-50 to-purple-100",
    route: "/customer-groups",
    helper: "Manage groups"
  },
  {
    title: "Total Customers",
    value: stats.totalCustomers || 0,
    accent: "text-teal-700",
    bg: "from-teal-50 to-teal-100",
    route: "/customers",
    helper: "View directory"
  },
  {
    title: "Website Enquiries",
    value: stats.totalWebsiteEnquiries || 0,
    accent: "text-rose-700",
    bg: "from-rose-50 to-pink-100",
    route: "/admin/website-enquiries",
    helper: "Open details"
  },
  {
    title: "Total Complaints",
    value: stats.totalComplaints || 0,
    accent: "text-blue-700",
    bg: "from-blue-50 to-blue-100",
    route: "/complaints",
    helper: "Open details"
  },
  {
    title: "Pending Complaints",
    value: stats.pendingComplaints || 0,
    accent: "text-amber-700",
    bg: "from-amber-50 to-yellow-100",
    route: "/complaints?status=pending",
    helper: "Open details"
  },
  {
    title: "Completed Complaints",
    value: stats.completedComplaints || 0,
    accent: "text-emerald-700",
    bg: "from-emerald-50 to-green-100",
    route: "/complaints?status=completed",
    helper: "Open details"
  },
  {
    title: "Today's Complaints",
    value: stats.todayComplaints || 0,
    accent: "text-violet-700",
    bg: "from-violet-50 to-fuchsia-100",
    route: "/complaints?today=1",
    helper: "Open details"
  }
]);
export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [showAllQuickActions, setShowAllQuickActions] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [groups, setGroups] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(API_BASE_URL + "/api/customer-groups");
        setGroups(res.data);
      } catch (err) {
        console.error("Failed to load groups on dashboard", err);
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        const [statsRes, notificationsRes] = await Promise.allSettled([
          axios.get(API_BASE_URL + "/api/dashboard", {
            params: { groupId: selectedGroupId || undefined }
          }),
          axios.get(API_BASE_URL + "/api/notifications", {
            params: { role: "admin" }
          })
        ]);

        setStats(
          statsRes.status === "fulfilled" ? (statsRes.value.data || {}) : {}
        );
        setNotifications(
          notificationsRes.status === "fulfilled" && Array.isArray(notificationsRes.value.data)
            ? notificationsRes.value.data
            : []
        );
      } catch (error) {
        console.error("Failed to load dashboard", error);
        setStats({});
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [selectedGroupId]);

  const cards = useMemo(() => buildCards(stats), [stats]);
  const visibleNotifications = useMemo(
    () => (showAllNotifications ? notifications : notifications.slice(0, 5)),
    [notifications, showAllNotifications]
  );

  const primaryQuickActions = [
    { label: "Complaint", to: "/create-complaint", color: "bg-blue-50", icon: "document" },
    { label: "Customer", to: "/create-customer", color: "bg-emerald-50", icon: "user" },
    { label: "Quote", to: "/admin/quotations", color: "bg-yellow-50", icon: "calendar" },
    { label: "Enquiries", to: "/admin/website-enquiries", color: "bg-rose-50", icon: "document" }
  ];

  const moreQuickActions = [
    { label: "Sales", to: "/admin/sales-invoices", color: "bg-rose-100", icon: "invoice" },
    { label: "Purchase", to: "/admin/purchase-orders", color: "bg-sky-100", icon: "cart" },
    { label: "Voucher", to: "/admin/vouchers", color: "bg-violet-100", icon: "receipt" },
    { label: "Supplier", to: "/admin/dealers", color: "bg-orange-100", icon: "truck" },
    { label: "Engineer", to: "/engineers", color: "bg-slate-100", icon: "helmet" },
    { label: "Accounts", to: "/admin/accounts", color: "bg-lime-100", icon: "ledger" },
    { label: "Stock", to: "/admin/material-stock", color: "bg-cyan-100", icon: "box" },
    { label: "Mapping", to: "/admin/product-mapping", color: "bg-rose-100", icon: "mapping" },
    { label: "Daily", to: "/admin/daily-updates", color: "bg-fuchsia-100", icon: "note" }
  ];

  const openNotification = async (notification) => {
    try {
      if (notification?._id && !notification.isRead) {
        await axios.put(API_BASE_URL + `/api/notifications/${notification._id}/read`);
        setNotifications((current) =>
          current.map((item) =>
            item._id === notification._id ? { ...item, isRead: true } : item
          )
        );
      }
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    } finally {
      navigate(resolveNotificationRoute(notification));
    }
  };

  const clearAllNotifications = async () => {
    try {
      await axios.delete(API_BASE_URL + "/api/notifications", {
        params: { role: "admin" }
      });
      setNotifications([]);
    } catch (error) {
      console.error("Failed to clear notifications", error);
    }
  };

  return (
    <>
      {/* ══════════════════════════════════════
          DESKTOP VIEW
      ══════════════════════════════════════ */}
      <div className="hidden lg:block space-y-6">
        {/* Hero Area */}
        <section className="bg-surface-container-low rounded-2xl p-xl mb-gutter relative overflow-hidden">
          {/* Background Decoration */}
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none">
            <img 
              className="object-cover w-full h-full" 
              alt="A sophisticated abstract gradient pattern" 
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuDqYE1hdDlzdRt4ZdIdlRw_Eo4SXIsupnYjzAdbklaOXcKsdy3sC3QydjBahQX32OHeTx_RUithlTvKRWI6HgR5YhuJqyLJqBjMW8tK3hX1vb6qmH304sRNlKo_WnYfz5M42ukyC6bTL-ZdaiwyKHJ38NoLeR-c8adkMfDDg3yUPVVZBXZF1ck4lY-fYx3_vg3J9frX2QA7f-Ya-CtRjkiyvi_3HloFbm3sTs-aFMDN2TDumabZUAZm3N3UFUqMEstCEYLEyXQSdDk"
            />
          </div>
          <div className="relative z-10">
            <span className="font-label-caps text-label-caps text-primary uppercase tracking-[0.2em] mb-2 block">Admin Workspace</span>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-xl">
              <h2 className="font-headline-xl text-headline-xl text-on-surface">Dashboard</h2>
              <div>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm text-slate-805 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 min-w-[200px]"
                >
                  <option value="">All Customer Groups</option>
                  {groups.map(g => (
                    <option key={g._id} value={g._id}>{g.groupName}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Metrics Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-md">
              {cards.map((card, idx) => {
                const borderColors = [
                  "border-l-primary-container",
                  "border-l-primary",
                  "border-l-tertiary-container",
                  "border-l-secondary",
                  "border-l-outline"
                ];
                const textColors = [
                  "text-primary",
                  "text-primary",
                  "text-error",
                  "text-secondary",
                  "text-on-surface"
                ];
                
                return (
                  <button 
                    key={card.title}
                    onClick={() => navigate(card.route)}
                    className={`bg-white p-6 rounded-xl border border-slate-200 shadow-sm border-l-4 ${borderColors[idx % borderColors.length]} group hover:shadow-md transition-shadow text-left w-full flex flex-col justify-between min-h-[140px]`}
                  >
                    <p className="font-label-caps text-[11px] text-on-surface-variant tracking-[0.05em] uppercase mb-4">{card.title}</p>
                    <div>
                      <h3 className={`font-numeric-display text-numeric-display ${textColors[idx % textColors.length]} mb-2`}>
                        {loading ? "..." : card.value}
                      </h3>
                      <span className="font-body-sm text-body-sm text-primary hover:underline flex items-center gap-1">
                        Open details
                        <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Notifications Section */}
        <section className="bg-white border border-slate-200 rounded-2xl p-xl shadow-sm">
          <div className="flex items-center justify-between mb-lg">
            <h3 className="font-title-md text-title-md text-on-surface flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">notifications_active</span>
              Notifications
            </h3>
            {notifications.length > 0 && (
              <button 
                onClick={clearAllNotifications}
                className="text-primary font-body-sm text-body-sm font-semibold hover:underline"
              >
                Clear all
              </button>
            )}
          </div>
          
          {notifications.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 py-10 text-center text-sm text-slate-500 font-semibold">
              You're all caught up!
            </div>
          ) : (
            <div className="space-y-4">
              {visibleNotifications.map((n, index) => {
                const badgeText = n.type ? n.type.toUpperCase().replace("-", " ") : "SYSTEM";
                const isTravel = n.type === "travel-created" || n.type === "travel-status";
                
                let badgeColor = "bg-primary text-white";
                let stripeColor = "border-l-primary";
                let itemBg = "bg-surface-container-low hover:bg-surface-container";
                
                if (isTravel) {
                  badgeColor = "bg-[#FF9800] text-white";
                  stripeColor = "border-l-[#FF9800]";
                  itemBg = "bg-[#FFF9F2] hover:bg-[#FFF4E5]";
                } else if (n.type === "report-submitted") {
                  badgeColor = "bg-emerald-600 text-white";
                  stripeColor = "border-l-emerald-500";
                  itemBg = "bg-emerald-50/50 hover:bg-emerald-50";
                } else if (n.type === "alert") {
                  badgeColor = "bg-red-600 text-white";
                  stripeColor = "border-l-red-500";
                  itemBg = "bg-rose-50/50 hover:bg-rose-50";
                }

                return (
                  <div 
                    key={n._id}
                    onClick={() => openNotification(n)}
                    className={`group relative border-l-4 ${stripeColor} p-md rounded-lg flex items-center justify-between transition-all cursor-pointer ${itemBg}`}
                  >
                    <div className="flex items-center gap-md col-span-2">
                      <span className={`${badgeColor} font-label-caps text-[10px] px-3 py-1 rounded-full shrink-0 h-fit`}>
                        {badgeText}
                      </span>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <p className="font-body-md text-body-md font-semibold text-on-surface">
                            {n.message}
                          </p>
                          {!n.isRead && (
                            <span className="bg-error text-white text-[10px] px-2 py-0.5 rounded font-bold uppercase shrink-0">
                              NEW
                            </span>
                          )}
                        </div>
                        <p className="font-body-sm text-body-sm text-on-surface-variant/70 mt-1">
                          {n.title ? n.title : "Click to view detail"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <button className="font-label-caps text-label-caps text-primary hover:underline uppercase block mb-1">
                        Open Linked Page
                      </button>
                      <span className="font-label-caps text-[10px] text-on-surface-variant/50">
                        {formatNotificationTime(n.createdAt) || "RECENT"}
                      </span>
                    </div>
                  </div>
                );
              })}

              {notifications.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllNotifications(c => !c)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  {showAllNotifications ? "View Less" : `View More (${notifications.length - 5} more)`}
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      {/* ══════════════════════════════════════
          MOBILE VIEW
      ══════════════════════════════════════ */}
      <div className="block lg:hidden space-y-6">
        {/* Greeting / Overview Title */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex items-end justify-between">
            <div>
              <span className="font-label-caps text-[11px] text-primary uppercase mb-1 block">ADMIN WORKSPACE</span>
              <h1 className="font-headline-lg text-headline-lg text-on-surface">Overview</h1>
            </div>
            <div className="flex gap-1 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-outline-variant"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-outline-variant"></div>
            </div>
          </div>
          <div>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 text-sm text-slate-805 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              <option value="">All Customer Groups</option>
              {groups.map(g => (
                <option key={g._id} value={g._id}>{g.groupName}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 5 metric cards in a single compact horizontal carousel stats bar */}
        <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-3 -mx-4 px-4 snap-x" style={{ display: "flex", flexDirection: "row" }}>
          {cards.map((card, idx) => {
            const isFirst = idx === 0;
            if (isFirst) {
              return (
                <button
                  key={card.title}
                  type="button"
                  onClick={() => navigate(card.route)}
                  className="snap-start min-w-[160px] flex-1 bg-primary text-white rounded-2xl p-4 shadow-lg shadow-primary/20 text-left active:scale-95 transition-transform font-label-caps"
                >
                  <span className="text-[10px] opacity-80 uppercase block mb-1">{card.title.replace(" Complaints", "").replace("Website ", "")}</span>
                  <div className="text-3xl font-bold mb-2">
                    {loading ? "..." : card.value}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] font-medium bg-white/20 w-fit px-2 py-0.5 rounded-full">
                    <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                    <span>View Details</span>
                  </div>
                </button>
              );
            }

            let helperColor = "text-primary";
            let helperText = "Real-time tracking";
            let icon = "";

            if (card.title.toLowerCase().includes("pending")) {
              helperColor = "text-amber-600";
              helperText = "Needs attention";
              icon = "warning";
            } else if (card.title.toLowerCase().includes("completed")) {
              helperColor = "text-emerald-600";
              helperText = "Resolved";
              icon = "check_circle";
            } else if (card.title.toLowerCase().includes("today")) {
              helperColor = "text-violet-600";
              helperText = "New today";
              icon = "schedule";
            }

            return (
              <button
                key={card.title}
                type="button"
                onClick={() => navigate(card.route)}
                className="snap-start min-w-[160px] flex-1 bg-white border border-outline-variant/50 rounded-2xl p-4 shadow-sm text-left active:scale-95 transition-transform font-label-caps"
              >
                <span className="text-[10px] text-on-surface-variant uppercase block mb-1">{card.title.replace(" Complaints", "").replace("Website ", "")}</span>
                <div className="text-3xl font-bold text-on-surface mb-2">
                  {loading ? "..." : card.value}
                </div>
                <div className={`flex items-center gap-1 text-[11px] font-semibold ${helperColor}`}>
                  {icon && <span className="material-symbols-outlined text-[14px]">{icon}</span>}
                  <span>{helperText}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Quick Actions */}
        <section className="mb-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-label-caps text-[11px] text-outline uppercase tracking-wider">Quick Actions</h2>
            <button 
              type="button" 
              onClick={() => setShowAllQuickActions(c => !c)}
              className="text-[11px] font-bold text-primary active:scale-95 transition-transform"
            >
              {showAllQuickActions ? "Show Less" : "Show More"}
            </button>
          </div>
          <div className="flex flex-row gap-6 overflow-x-auto hide-scrollbar -mx-4 px-4 pb-2" style={{ display: "flex", flexDirection: "row" }}>
            {primaryQuickActions.map(({ label, to, icon }) => {
              const getIconName = (name) => {
                if (name === "document") return "description";
                if (name === "user") return "person_add";
                if (name === "calendar") return "request_quote";
                return "mail";
              };
              return (
                <div key={label} className="flex flex-col items-center gap-2 min-w-[72px]">
                  <button 
                    onClick={() => navigate(to)}
                    className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center active:scale-95 transition-transform shadow-sm"
                  >
                    <span className="material-symbols-outlined text-2xl">{getIconName(icon)}</span>
                  </button>
                  <span className="text-[11px] font-semibold text-on-surface-variant text-center">{label}</span>
                </div>
              );
            })}
            
            {!showAllQuickActions && (
              <div className="flex flex-col items-center gap-2 min-w-[72px]">
                <button 
                  onClick={() => setShowAllQuickActions(true)}
                  className="w-14 h-14 rounded-full bg-surface-container-high text-on-surface flex items-center justify-center active:scale-95 transition-transform shadow-sm"
                >
                  <span className="material-symbols-outlined text-2xl">more_horiz</span>
                </button>
                <span className="text-[11px] font-semibold text-on-surface-variant text-center">More</span>
              </div>
            )}
          </div>

          {showAllQuickActions && (
            <div className="mt-4 flex flex-row gap-6 overflow-x-auto hide-scrollbar -mx-4 px-4 pb-2 border-t border-outline-variant/10 pt-4" style={{ display: "flex", flexDirection: "row" }}>
              {moreQuickActions.map(({ label, to, icon }) => {
                const getIconName = (name) => {
                  switch(name) {
                    case "invoice": return "receipt_long";
                    case "cart": return "shopping_cart";
                    case "receipt": return "payments";
                    case "truck": return "local_shipping";
                    case "helmet": return "engineering";
                    case "ledger": return "book";
                    case "stock": return "inventory_2";
                    case "mapping": return "grid_view";
                    case "daily": return "event_note";
                    default: return "settings";
                  }
                };
                return (
                  <div key={label} className="flex flex-col items-center gap-2 min-w-[72px]">
                    <button 
                      onClick={() => navigate(to)}
                      className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center active:scale-95 transition-transform shadow-sm"
                    >
                      <span className="material-symbols-outlined text-2xl">{getIconName(icon)}</span>
                    </button>
                    <span className="text-[11px] font-semibold text-on-surface-variant text-center">{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Recent Activity (Timeline Style) */}
        <section className="bg-white rounded-3xl shadow-sm border border-outline-variant/30 overflow-hidden mb-6">
          <div className="flex justify-between items-center px-5 py-5 border-b border-outline-variant/20">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl">
                <span className="material-symbols-outlined text-primary text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>history</span>
              </div>
              <div>
                <h2 className="font-headline-sm text-headline-sm text-on-surface">Recent Activity</h2>
                <p className="text-[11px] text-outline">Latest status updates from site</p>
              </div>
            </div>
            {notifications.length > 0 && (
              <button 
                onClick={clearAllNotifications}
                className="text-red-500 font-body-md text-[13px] font-semibold hover:bg-red-50/10 px-3 py-1.5 rounded-lg transition-colors active:scale-95"
              >
                Clear All
              </button>
            )}
          </div>
          
          {notifications.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500 font-semibold bg-slate-50/50">
              You're all caught up!
            </div>
          ) : (
            <div className="px-5 py-6 space-y-8 relative">
              {/* Vertical Timeline Line */}
              <div className="absolute left-[33px] top-6 bottom-6 w-[2px] bg-outline-variant/30"></div>
              
              {(showAllNotifications ? notifications : notifications.slice(0, 5)).map((n) => {
                const isTravel = n.type === "travel-created" || n.type === "travel-status";
                const isReport = n.type === "report-submitted" || n.type === "report-approved";
                const isAlert = n.type === "alert" || n.type === "report-changes-requested";
                
                let iconName = "notifications";
                let iconColor = "text-primary bg-primary/10";
                let badgeBg = "bg-blue-50 text-primary";
                let badgeText = n.type ? n.type.toUpperCase().replace("-", " ") : "SYSTEM";
                
                if (isTravel) {
                  const msg = String(n.message || "").toLowerCase();
                  if (msg.includes("started") || msg.includes("departure")) {
                    iconName = "directions_car";
                  } else if (msg.includes("site") || msg.includes("arrived") || msg.includes("reached")) {
                    iconName = "location_on";
                  } else {
                    iconName = "navigation";
                  }
                  iconColor = "text-orange-600 bg-orange-100";
                  badgeBg = "bg-orange-50 text-orange-700";
                } else if (isReport) {
                  iconName = "task_alt";
                  iconColor = "text-emerald-700 bg-emerald-100";
                  badgeBg = "bg-emerald-50 text-emerald-800";
                } else if (isAlert) {
                  iconName = "warning";
                  iconColor = "text-rose-700 bg-rose-100";
                  badgeBg = "bg-rose-50 text-rose-800";
                }
                
                return (
                  <div 
                    key={n._id}
                    onClick={() => openNotification(n)}
                    className="relative flex gap-4 cursor-pointer group active:opacity-85"
                  >
                    <div className={`z-10 w-7 h-7 rounded-full border-2 border-white flex items-center justify-center shadow-sm mt-0.5 shrink-0 ${iconColor}`}>
                      <span className="material-symbols-outlined text-[16px]">{iconName}</span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1 gap-2">
                        <h3 className="text-[14px] font-bold text-on-surface leading-snug group-hover:text-primary transition-colors">
                          {n.message}
                        </h3>
                        <span className="text-[11px] text-outline font-medium shrink-0">
                          {formatNotificationTime(n.createdAt) || "Just now"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wider ${badgeBg}`}>
                          {badgeText}
                        </span>
                        {n.title && (
                          <span className="text-[12px] text-on-surface-variant truncate">
                            {n.title}
                          </span>
                        )}
                        {!n.isRead && (
                          <span className="bg-error text-white text-[9px] px-1.5 py-0.2 rounded font-bold uppercase shrink-0">
                            NEW
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {notifications.length > 5 && (
            <div className="bg-surface-container-low/40 p-4 text-center border-t border-outline-variant/10">
              <button 
                type="button"
                onClick={() => setShowAllNotifications(c => !c)}
                className="text-outline font-bold text-[11px] uppercase tracking-widest hover:text-primary transition-colors"
              >
                {showAllNotifications ? "Show Less" : "Show Older Activity"}
              </button>
            </div>
          )}
        </section>
      </div>

    </>
  );
}
